const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_TIMELINE_ROW_MS = 60 * 1000;

export const normalizeTimetableTargetDate = (raw) => {
  if (!raw) return null;
  const str = String(raw);
  const match = str.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (match) {
    const [, year, month, day] = match;
    const pad = (value) => value.padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

export const parseDurationMs = (raw) => {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, raw * 60 * 1000);
  const text = String(raw).trim();
  const japaneseMinutes = text.match(/^(\d+)\s*分$/);
  if (japaneseMinutes) {
    return Number(japaneseMinutes[1]) * 60 * 1000;
  }
  const parts = text.split(':').map((value) => Number(value) || 0);
  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return Math.max(0, (hours * 3600 + minutes * 60 + seconds) * 1000);
  }
  if (parts.length === 2) {
    const [hours, minutes] = parts;
    return Math.max(0, (hours * 60 + minutes) * 60 * 1000);
  }
  const numeric = Number(text);
  return Number.isFinite(numeric) ? Math.max(0, numeric * 60 * 1000) : 0;
};

export const parseTimeMinutes = (timeStr) => {
  const match = String(timeStr || '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 60 + minute;
};

export const buildJstDate = (ymd, timeStr) => {
  if (!ymd || !timeStr) return null;
  const pieces = String(timeStr).split(':').map((value) => value.trim());
  if (!pieces[0]) return null;
  const hour = pieces[0].padStart(2, '0');
  const minute = (pieces[1] || '00').padStart(2, '0');
  const second = (pieces[2] || '00').padStart(2, '0');
  const date = new Date(`${ymd}T${hour}:${minute}:${second}+09:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatStartTime = (raw) => {
  if (!raw) return '';
  return String(raw).trim().slice(0, 5);
};

export const addMinutesToTime = (timeStr, deltaMinutes) => {
  if (!timeStr) return '';
  const match = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return String(timeStr).trim().slice(0, 5);
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return String(timeStr).trim().slice(0, 5);
  const delta = Number(deltaMinutes);
  if (!Number.isFinite(delta) || delta === 0) {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }
  const wrapped = (((hour * 60 + minute + delta) % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
};

export const buildAdjustmentMap = (adjustments = []) => {
  const map = new Map();
  (adjustments || []).forEach((adjustment) => {
    const key = String(adjustment?.row_id ?? adjustment?.rowId ?? '').trim();
    if (!key) return;
    const minutes = Number(adjustment?.minutes ?? 0);
    if (!Number.isFinite(minutes) || minutes === 0) return;
    map.set(key, Math.round(minutes));
  });
  return map;
};

export const buildMemoMap = (memos = []) => {
  const map = new Map();
  (memos || []).forEach((memo) => {
    const key = String(memo?.row_id ?? memo?.rowId ?? '').trim();
    if (key) map.set(key, memo);
  });
  return map;
};

export const getRowId = (row) => String(row?.id ?? row?.row_id ?? row?.rowId ?? '').trim();

export const getRowStartTime = (row) => {
  if (!row) return '';
  const direct = row.startTime ?? row.start_time;
  if (direct !== undefined && direct !== null && String(direct).trim()) return String(direct).trim();
  const cellValue = row.cells?.startTime?.value;
  return cellValue !== undefined && cellValue !== null ? String(cellValue).trim() : '';
};

export const getRowDurationValue = (row) => {
  if (!row) return '';
  if (row.duration !== undefined && row.duration !== null && String(row.duration).trim()) return row.duration;
  if (row.durationText !== undefined && row.durationText !== null && String(row.durationText).trim()) {
    return row.durationText;
  }
  const cellValue = row.cells?.duration?.value;
  return cellValue !== undefined && cellValue !== null ? cellValue : '';
};

export const getRowItemText = (row) => {
  if (!row) return '';
  const rawItem = row.item !== undefined && row.item !== null ? String(row.item) : '';
  if (rawItem.trim()) return rawItem;
  const cellValue = row.cells?.item?.value;
  return cellValue !== undefined && cellValue !== null && String(cellValue).trim() ? String(cellValue) : '';
};

export const applyRowAdjustments = (rows = [], adjustmentsMap) => {
  if (!rows.length) return [];
  const map = adjustmentsMap instanceof Map ? adjustmentsMap : new Map();
  let offset = 0;
  return rows.map((row) => {
    const offsetForRow = offset;
    const baseStartTime = getRowStartTime(row);
    const rowId = getRowId(row);
    const nextRow = {
      ...row,
      id: rowId || row?.id,
      adjustedStartTime: baseStartTime ? addMinutesToTime(baseStartTime, offsetForRow) : '',
      adjustedOffsetMinutes: offsetForRow,
    };
    const delta = map.get(rowId) || 0;
    if (Number.isFinite(delta)) offset += delta;
    return nextRow;
  });
};

export const buildTimelineRows = (rows = [], ymd) => {
  let dayOffset = 0;
  let previousMinutes = null;
  const timeline = [];

  rows.forEach((row) => {
    const rowId = getRowId(row);
    const rawStartTime = getRowStartTime(row);
    const baseTime = row.adjustedStartTime || rawStartTime;
    const startMinutes = parseTimeMinutes(baseTime);
    if (startMinutes === null) return;
    if (previousMinutes !== null && startMinutes < previousMinutes) {
      dayOffset += 1;
    }
    previousMinutes = startMinutes;

    const start = buildJstDate(ymd, baseTime);
    if (!start) return;
    const adjustedOffsetMinutes = Number(row.adjustedOffsetMinutes || 0);
    const formattedRawStartTime = formatStartTime(rawStartTime);
    const formattedStartTime = formatStartTime(baseTime);
    const hasAdjustedTime =
      adjustedOffsetMinutes !== 0 &&
      Boolean(row.adjustedStartTime) &&
      Boolean(formattedRawStartTime) &&
      Boolean(formattedStartTime);
    const durationValue = getRowDurationValue(row);
    timeline.push({
      ...row,
      id: rowId || row.id,
      rowId,
      startMs: start.getTime() + dayOffset * DAY_MS,
      durationMs: parseDurationMs(durationValue),
      startTime: formattedStartTime,
      rawStartTime: formattedRawStartTime,
      displayStartTime: baseTime,
      originalStartTime: rawStartTime,
      durationLabel: durationValue !== undefined && durationValue !== null ? String(durationValue).trim() : '',
      adjustedOffsetMinutes,
      hasAdjustedTime,
      item: getRowItemText(row),
    });
  });

  const sorted = timeline.sort((a, b) => a.startMs - b.startMs);
  return sorted.map((item, index) => {
    const nextStartMs = sorted[index + 1]?.startMs;
    const fallbackMs =
      Number.isFinite(nextStartMs) && nextStartMs > item.startMs
        ? Math.max(MIN_TIMELINE_ROW_MS, nextStartMs - item.startMs)
        : MIN_TIMELINE_ROW_MS;
    return {
      ...item,
      endMs: item.startMs + (item.durationMs || fallbackMs),
    };
  });
};

export const findCurrentTimelineRowId = (rows = [], nowMs = Date.now()) => {
  let candidate = null;
  rows.forEach((row) => {
    if (row.startMs <= nowMs && nowMs < row.endMs) {
      if (!candidate || candidate.startMs < row.startMs) {
        candidate = { id: row.id, startMs: row.startMs };
      }
    }
  });
  return candidate?.id || null;
};

export const findNextTimelineStartMs = (rows = [], nowMs = Date.now()) => {
  let next = null;
  rows.forEach((row) => {
    if (row.startMs > nowMs && (next === null || row.startMs < next)) {
      next = row.startMs;
    }
  });
  return next;
};
